/**
 * Import Progress Indicator — the panel itself (presentational).
 *
 * Orchestration (store, stream, observer) lives in ImportProgressIndicator;
 * this file only lays the rows out: header with the live chip and count, the
 * batch ribbon from two imports up, live rows, failures, recent completions.
 *
 * Keyboard: every control is a native button; Escape minimises the panel.
 * Motion: every animation is `motion-safe:` (reduced motion = static bars).
 */

import { useId, type KeyboardEvent } from 'react'

import { ChevronDown, ChevronUp, ListX, Maximize2, Minimize2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ConnectionState } from '@/platform/websocket/syncClient'

import { ImportBatchSummary } from './ImportBatchSummary'
import { ImportFailedItem, type FailedImport } from './ImportFailedItem'
import { ImportItem, type ActiveImport } from './ImportItem'
import { ImportPanelConnectionState } from './ImportPanelConnectionState'
import { FOCUS_RING_CLASS, type ImportRowState } from './ImportRowParts'
import { RecentlyCompletedSection, type CompletedImportEntry } from './RecentlyCompletedSection'
import { IMPORT_PROGRESS_PANEL_Z_CLASS } from '../constants/overlay-layers'

interface ImportProgressPanelProps {
  connectionState: ConnectionState
  activeImports: ActiveImport[]
  failedImports: FailedImport[]
  completedImports: CompletedImportEntry[]
  isExpanded: boolean
  isMinimized: boolean
  onToggleExpanded: () => void
  onToggleMinimized: () => void
  onMinimize: () => void
  /** Epoch ms of the shared one-second clock. */
  now: number
  /** Latest polite screen-reader news (queued / completed / failed). */
  announcement: string
  formatRelativeTime: (date: Date) => string
  onDismissFailed: (jobId: string) => void
  onDismissCompleted: (jobId: string) => void
  /** Clears every finished (done or failed) notification. */
  onClearFinished: () => void
  /** Hides the panel until a new import starts. */
  onClose: () => void
  onRetry: (entry: FailedImport) => Promise<void>
}

const liveRowState = (row: ActiveImport): ImportRowState => {
  if (row.currentStage === 'waiting_provider') return 'waiting'
  return row.status === 'processing' ? 'running' : 'queued'
}

const ICON_BUTTON_CLASS = `p-1.5 rounded-md text-crypto-muted hover:bg-crypto-border/40 hover:text-crypto-text ${FOCUS_RING_CLASS}`

export const ImportProgressPanel = ({
  connectionState,
  activeImports,
  failedImports,
  completedImports,
  isExpanded,
  isMinimized,
  onToggleExpanded,
  onToggleMinimized,
  onMinimize,
  now,
  announcement,
  formatRelativeTime,
  onDismissFailed,
  onDismissCompleted,
  onClearFinished,
  onClose,
  onRetry,
}: ImportProgressPanelProps) => {
  const { t } = useTranslation('imports')
  const contentId = useId()
  const titleId = useId()
  const showContent = isExpanded && !isMinimized

  const batchStates: ImportRowState[] = [
    ...activeImports.map(liveRowState),
    ...failedImports.map((): ImportRowState => 'failed'),
    ...completedImports.map((): ImportRowState => 'done'),
  ]

  const collapseLabel = isExpanded
    ? t('progressIndicator.collapse', 'Collapse')
    : t('progressIndicator.expand', 'Expand')
  const clearLabel = t('progressIndicator.clearFinished', 'Clear notifications')
  const closeLabel = t('progressIndicator.close', 'Close (opens again with the next import)')
  const minimizeLabel = isMinimized
    ? t('progressIndicator.expand', 'Expand')
    : t('progressIndicator.minimize', 'Minimize')

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && !isMinimized) {
      event.stopPropagation()
      onMinimize()
    }
  }

  return (
    <section
      data-testid="import-progress-indicator"
      data-active-count={activeImports.length}
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      className={`fixed bottom-4 right-4 ${IMPORT_PROGRESS_PANEL_Z_CLASS} flex flex-col overflow-hidden rounded-xl border border-crypto-border bg-crypto-card text-crypto-text shadow-2xl ${
        isMinimized ? 'w-auto' : 'left-4 sm:left-auto sm:w-96 max-h-[70vh]'
      }`}
    >
      {/* Polite news for screen readers: transitions only, never the ticking numbers. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>

      <header className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-crypto-border">
        <div className="flex min-w-0 items-center gap-2">
          <h2 id={titleId} className="truncate text-sm font-semibold text-crypto-text">
            {t('progressIndicator.activeImports', 'Active imports')}
          </h2>
          <span className="rounded-full bg-crypto-accent/15 px-2 py-0.5 text-xs font-semibold tabular-nums text-crypto-accent">
            {activeImports.length}
          </span>
          <ImportPanelConnectionState connectionState={connectionState} />
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={onToggleExpanded}
            className={ICON_BUTTON_CLASS}
            title={collapseLabel}
            aria-label={collapseLabel}
            aria-expanded={showContent}
            aria-controls={contentId}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            ) : (
              <ChevronUp className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={onToggleMinimized}
            className={ICON_BUTTON_CLASS}
            title={minimizeLabel}
            aria-label={minimizeLabel}
          >
            {isMinimized ? (
              <Maximize2 className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Minimize2 className="w-4 h-4" aria-hidden="true" />
            )}
          </button>
          {failedImports.length + completedImports.length > 0 && (
            <button
              type="button"
              onClick={onClearFinished}
              className={ICON_BUTTON_CLASS}
              title={clearLabel}
              aria-label={clearLabel}
              data-testid="import-progress-clear-finished"
            >
              <ListX className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className={ICON_BUTTON_CLASS}
            title={closeLabel}
            aria-label={closeLabel}
            data-testid="import-progress-close"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      {showContent && (
        <div id={contentId} className="flex min-h-0 flex-col">
          <ImportBatchSummary states={batchStates} />
          <div
            className="p-3 space-y-2 max-h-[50vh] overflow-y-auto overscroll-contain"
            data-testid="import-progress-indicator-content"
          >
            {activeImports.map((import_) => (
              <ImportItem
                key={import_.jobId}
                import_={import_}
                formatRelativeTime={formatRelativeTime}
                now={now}
              />
            ))}

            {/* Failed imports — an explicit ERROR state (danger, alert role) so a
                failure is never mistaken for success. Retry + dismiss. */}
            {failedImports.map((entry) => (
              <ImportFailedItem
                key={entry.jobId}
                entry={entry}
                onDismiss={() => onDismissFailed(entry.jobId)}
                formatRelativeTime={formatRelativeTime}
                onRetry={entry.sourceId ? () => onRetry(entry) : undefined}
              />
            ))}

            {/* Recently completed imports — see RecentlyCompletedSection. */}
            <RecentlyCompletedSection
              entries={completedImports}
              onDismiss={onDismissCompleted}
              formatRelativeTime={formatRelativeTime}
            />
          </div>
        </div>
      )}
    </section>
  )
}
